/**
 * ForecastTable — matrix "III. CASH FLOWS FORECAST" style theo design
 * `plan/report/erp-report-cash.jsx` (CashflowsForecastTable): header forest
 * sticky chữ trắng, cột [label | unit | woy...], band full-row cho section
 * "A./B." (vàng đậm) + subsection "Cash Inflows/Outflows" (vàng nhạt nghiêng),
 * hàng Opening nghiêng nền xám, hàng Surplus/(Deficit) tổng nền xanh đậm chữ
 * 700, label thụt cấp bằng padding, số IBM Plex Mono ngoặc đỏ khi âm, ô
 * trống "—". DATA giữ nguyên API (template view BE sort + format flags);
 * loại dòng suy từ flags bold/italic/red + độ thụt leading-spaces của label
 * template (KHÔNG đổi contract BE).
 */
import { Box, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material'

import { fmtNum } from '../pnl/format'
import type { PnLRow } from '../pnl/PnLTable'
import { FONT_MONO, FONT_SANS, RPT } from './reportTokens'

const HEAD_SX = {
  bgcolor: RPT.forest,
  color: '#fff',
  fontFamily: FONT_SANS,
  fontWeight: 600,
  fontSize: '0.68rem',
  letterSpacing: '0.02em',
  lineHeight: 1.2,
  py: 0.75,
  px: 1.25,
  border: 0,
  borderBottom: `1px solid ${RPT.forest}`,
  whiteSpace: 'nowrap',
  position: 'sticky',
  top: 0,
  zIndex: 2,
} as const

const CELL_SX = {
  fontSize: '0.7rem',
  lineHeight: 1.35,
  py: 0.45,
  px: 1.25,
  border: 0,
  borderBottom: `1px solid ${RPT.grid}`,
  whiteSpace: 'nowrap',
  verticalAlign: 'top',
} as const

/** Loại dòng theo design — suy từ format flags template + độ thụt label:
 *  red → Opening (label "Opening...") / summary (Cash|Closing Surplus);
 *  bold thường → section "A./B." (depth 0) / subsection Inflows-Outflows;
 *  italic thường (depth 21) → indent (SI/Booking, Contracted Shipment);
 *  còn lại (bold+italic, depth 12) → data line item. */
type RowKind = 'opening' | 'section' | 'subsection' | 'summary' | 'indent' | 'data'

function classify(row: PnLRow): RowKind {
  const label = String(row.label ?? '')
  const depth = label.length - label.trimStart().length
  if (row.red) return label.trimStart().startsWith('Opening') ? 'opening' : 'summary'
  if (row.bold && !row.italic) return depth === 0 ? 'section' : 'subsection'
  if (row.italic && !row.bold) return 'indent'
  return 'data'
}

/** Padding-left label theo cấp thụt (design thay leading-spaces bằng padding). */
const LABEL_PAD: Record<RowKind, number> = {
  opening: 12,
  summary: 12,
  section: 12,
  subsection: 12,
  data: 24,
  indent: 44,
}

interface ForecastTableProps {
  /** Nhãn cột tuần (woy, vd "2026-24") — key giá trị tương ứng `w<index>`. */
  weeks: string[]
  rows: PnLRow[]
}

export default function ForecastTable({ weeks, rows }: ForecastTableProps) {
  return (
    <Box sx={{ overflow: 'auto', border: `1px solid ${RPT.border}`, borderRadius: '6px' }}>
      <Table
        size="small"
        sx={{ borderCollapse: 'separate', borderSpacing: 0, minWidth: 1050, tableLayout: 'auto' }}
      >
        <colgroup>
          <col style={{ width: '24%', minWidth: 280 }} />
          <col style={{ width: '4%', minWidth: 46 }} />
          {weeks.map((w) => (
            <col key={w} style={{ width: `${72 / Math.max(weeks.length, 1)}%`, minWidth: 88 }} />
          ))}
        </colgroup>
        <TableHead>
          <TableRow>
            {/* 2 cột row-header để trống label (PBI/design parity). */}
            <TableCell sx={HEAD_SX} />
            <TableCell sx={HEAD_SX} />
            {weeks.map((w) => (
              <TableCell key={w} align="right" sx={{ ...HEAD_SX, fontFamily: FONT_MONO, px: 1 }}>
                {w}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {(() => {
            // Theo PBI matrix: cột đầu (lv1) là row-group → label lặp ở các dòng
            // tách KGs/USD chỉ hiển thị 1 lần (dòng sau để TRỐNG). Web trước đó in
            // mọi dòng → dup text. Track label dòng trước để blank khi trùng.
            let prevLabel = ''
            return rows.map((row, ri) => {
            const kind = classify(row)
            const label = String(row.label ?? '').trim()

            // Section/subsection = band full-row (design colSpan cả bảng) — reset
            // group, label kế tiếp luôn hiển thị.
            if (kind === 'section' || kind === 'subsection') {
              prevLabel = ''
              const band =
                kind === 'section'
                  ? { bgcolor: RPT.sectionBg, color: RPT.sectionText, fontWeight: 600, fontStyle: 'normal', borderBottom: `1px solid ${RPT.sectionBorder}` }
                  : { bgcolor: RPT.subsectionBg, color: RPT.subsectionText, fontWeight: 500, fontStyle: 'italic', borderBottom: `1px solid ${RPT.subsectionBorder}` }
              return (
                <TableRow key={ri}>
                  <TableCell
                    colSpan={2 + weeks.length}
                    sx={{ ...CELL_SX, ...band, fontFamily: FONT_SANS, py: kind === 'section' ? 0.6 : 0.4 }}
                  >
                    {label}
                  </TableCell>
                </TableRow>
              )
            }

            // Blank label khi trùng dòng trước (PBI row-group), giữ unit + số.
            const dupLabel = label !== '' && label === prevLabel
            prevLabel = label

            const isOpening = kind === 'opening'
            const isSummary = kind === 'summary'
            const isIndent = kind === 'indent'
            const rowBg = isSummary ? RPT.summaryBg : isOpening ? RPT.openingBg : '#fff'
            const labelSx = {
              ...CELL_SX,
              pl: `${LABEL_PAD[kind]}px`,
              fontFamily: FONT_SANS,
              fontWeight: isSummary ? 700 : isOpening || isIndent ? 500 : 600,
              fontStyle: isOpening ? 'italic' : 'normal',
              color: isIndent ? RPT.sub : RPT.ink,
              bgcolor: rowBg,
            }
            return (
              <TableRow key={ri}>
                <TableCell sx={labelSx}>{dupLabel ? '' : label}</TableCell>
                {/* Cột unit (USD/KGs) — chữ nhỏ màu phụ. */}
                <TableCell
                  sx={{
                    ...CELL_SX,
                    px: 1,
                    fontFamily: FONT_SANS,
                    fontSize: '0.62rem',
                    letterSpacing: '0.03em',
                    fontWeight: 500,
                    color: RPT.sub,
                    bgcolor: rowBg,
                  }}
                >
                  {row.unit == null ? '' : String(row.unit)}
                </TableCell>
                {weeks.map((w, wi) => {
                  const raw = row[`w${wi}`]
                  const empty = raw == null || raw === ''
                  const r = empty ? { text: '—', neg: false } : fmtNum(Number(raw))
                  const zero = !empty && Number(raw) === 0
                  return (
                    <TableCell
                      key={w}
                      align="right"
                      sx={{
                        ...CELL_SX,
                        px: 1,
                        fontFamily: FONT_MONO,
                        fontVariantNumeric: 'tabular-nums',
                        fontWeight: isSummary ? 700 : 400,
                        fontStyle: isOpening ? 'italic' : 'normal',
                        color: empty || zero ? RPT.axis : r.neg ? RPT.rose : RPT.ink,
                        bgcolor: rowBg,
                      }}
                    >
                      {r.text}
                    </TableCell>
                  )
                })}
              </TableRow>
            )
          })
          })()}
        </TableBody>
      </Table>
    </Box>
  )
}
